import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { ButtonIcon, ButtonText, OutlineCard } from '@morressier/alembic';
import dateFormat from 'dateformat';
import Divider from '../Divider';
import {
  IDecisionHistoryItem,
  IRoleType,
  ISubmissionFile
} from '@publishing-engine/domain/models';
import { decisionStatusBadges } from '../Review/Badges';
import ShowAttachementFiles from './ShowAttachementFiles';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { Overlay } from '../Overlay';
import { Layout } from '../Layout';
import { Preview } from './ActivityTab/EmailViewModal';
import { useQuery } from '@tanstack/react-query';
import { fetchEmailByEventId } from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';

interface Props {
  decisionHistory: IDecisionHistoryItem[];
  roleContext: IRoleType;
}

const DecisionHistoryFullView = ({ decisionHistory, roleContext }: Props) => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailEventId, setEmailEventId] = useState('');
  const [showAttachments, setShowAttachments] = useState<
    ISubmissionFile[] | null
  >(null);
  const { submissionId } = useSubmissionData();
  const { getFieldState } = useSubmissionFields();
  const submittingAuthorEmail = getFieldState('SubmittingAuthor')?.value as
    | string
    | undefined;
  const {
    data: emailLog,
    isLoading,
    isError
  } = useQuery({
    queryKey: [
      'email-by-event',
      emailEventId,
      roleContext,
      submittingAuthorEmail
    ],
    queryFn: () =>
      fetchEmailByEventId(
        submissionId,
        emailEventId,
        roleContext,
        submittingAuthorEmail
      ),
    enabled: !!emailEventId && !!submittingAuthorEmail
  });
  return (
    <>
      {showAttachments && (
        <ShowAttachementFiles
          attachments={showAttachments}
          onClose={() => setShowAttachments(null)}
        />
      )}
      {emailEventId && isEmailModalOpen && (
        <Overlay>
          <Flex direction="column" backgroundColor="surfaceSecondaryFill">
            <Layout width="800px">
              <Flex justifyContent="space-between" alignItems="center" p={6}>
                <ButtonText
                  variant="secondary"
                  onClick={() => {
                    setEmailEventId('');
                    setIsEmailModalOpen(false);
                  }}
                  iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
                >
                  Back to overview
                </ButtonText>
              </Flex>
              <Box flex={1} overflowY="auto" p={6}>
                <>
                  {isLoading ? (
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      height="100%"
                    >
                      <Spinner />
                    </Flex>
                  ) : isError ? (
                    <Text color="red.500">Error loading email</Text>
                  ) : (
                    <Preview emailLog={emailLog?.items} />
                  )}
                </>
              </Box>
            </Layout>
          </Flex>
        </Overlay>
      )}
      {decisionHistory?.length > 0 ? (
        decisionHistory?.map(history => {
          const decision = history?.decision;
          const badgeKey =
            decision?.type === 'RevisionRequested' &&
            decision?.formPayload?.revisionSeverity
              ? `${decision.formPayload.revisionSeverity}Revision`
              : decision?.type;

          return (
            <OutlineCard
              mt={3}
              key={`${history.manuscriptId}-${history.revisionNumber}-${
                history.emailEventId ?? history.decision?.type
              }`}
            >
              <Flex gap={2} flexDirection="column" padding={2}>
                <Flex justifyContent="space-between" alignItems="center">
                  <Text textStyle="headingItem">
                    {`${history?.manuscriptId} | ${
                      history.revisionNumber === 0
                        ? 'Original Submission'
                        : `Revision - ${history.revisionNumber}`
                    } (`}

                    {dateFormat(history.dateEntered, 'mmm d, yyyy')}
                    {' to '}
                    {history.dateCompleted
                      ? dateFormat(history.dateCompleted, 'mmm d, yyyy')
                      : 'Ongoing'}
                    {')'}
                  </Text>
                </Flex>

                <Flex
                  justifyContent="space-between"
                  gap={2}
                  flexDirection="column"
                >
                  <Flex gap={2} flexDirection="column">
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex gap={2} alignItems="center">
                        <Text textStyle="headingLine">Decision:</Text>
                        {decisionStatusBadges[badgeKey] || '--'}

                        <Flex alignItems={'center'}>
                          <Text textStyle="headingLine">Decision Email:</Text>
                          <ButtonIcon
                            icon={<FontAwesomeIcon icon={faEnvelope} />}
                            ariaLabel="email"
                            onClick={() => {
                              setEmailEventId(history?.emailEventId ?? '');
                              setIsEmailModalOpen(true);
                            }}
                          />
                        </Flex>
                      </Flex>
                    </Flex>
                    {history?.decision?.formPayload?.rejectionReason && (
                      <Flex gap={2} alignItems={'flex-start'}>
                        <Text textStyle="headingLine">Rejection Reason:</Text>
                        <Text whiteSpace="pre-line">
                          {history.decision.formPayload.rejectionReason}
                        </Text>
                      </Flex>
                    )}
                    {history?.decision?.formPayload?.withdrawalReason && (
                      <Flex gap={2} alignItems={'flex-start'}>
                        <Text textStyle="headingLine">Withdrawal Reason:</Text>
                        <Text whiteSpace="pre-line">
                          {history.decision.formPayload.withdrawalReason}
                        </Text>
                      </Flex>
                    )}
                    <Flex gap={2} alignItems={'flex-start'}>
                      <Text textStyle="headingLine">Response to Author:</Text>
                      <Text flex={1} minWidth={0}>
                        {history?.decision?.formPayload?.editorComment ?? '--'}
                      </Text>
                    </Flex>
                    <Flex
                      gap={2}
                      justifyContent={'space-between'}
                      alignItems="center"
                      color="gray.500"
                    >
                      <Text textStyle="textContext">
                        Added by {history?.adminDetails?.firstName}{' '}
                        {history?.adminDetails?.lastName} (
                        {history?.adminDetails?.roleName}) on{' '}
                        {dateFormat(
                          history?.adminDetails?.decisionDate,
                          'mmm d, yyyy'
                        )}
                      </Text>

                      <ButtonText
                        textStyle="headingLine"
                        disabled={
                          !history?.decision?.formPayload?.editorAttachments ||
                          history?.decision?.formPayload?.editorAttachments
                            ?.length === 0
                        }
                        onClick={() =>
                          setShowAttachments(
                            history?.decision?.formPayload?.editorAttachments ||
                              []
                          )
                        }
                      >
                        Files (
                        {history?.decision?.formPayload?.editorAttachments
                          ?.length ?? 0}
                        )
                      </ButtonText>
                    </Flex>
                  </Flex>
                  {(history.authorDetails !== null ||
                    history.authorResponseDetails !== null) && (
                    <>
                      {Divider}
                      <Flex flexDirection="column" gap={2}>
                        <Flex gap={1} alignItems={'flex-start'}>
                          <Text textStyle="headingLine">Author Response:</Text>
                          <Text flex={1} minWidth={0}>
                            {history?.authorResponseDetails?.authorResponse}
                          </Text>
                        </Flex>

                        <Flex
                          gap={2}
                          alignItems="center"
                          justifyContent={'space-between'}
                          color="gray.500"
                        >
                          <Text textStyle="textContext">
                            Added by {history?.authorDetails?.firstName}{' '}
                            {history?.authorDetails?.lastName} (
                            {history?.authorDetails?.roleName}) on{' '}
                            {dateFormat(
                              history?.authorDetails?.revisionSubmittedDate,
                              'mmm d, yyyy'
                            )}
                          </Text>

                          <ButtonText
                            textStyle="headingLine"
                            disabled={
                              !history?.authorResponseDetails
                                ?.authorResponseAttachments ||
                              history?.authorResponseDetails
                                ?.authorResponseAttachments?.length === 0
                            }
                            onClick={() =>
                              setShowAttachments(
                                history?.authorResponseDetails
                                  ?.authorResponseAttachments || []
                              )
                            }
                          >
                            Files (
                            {history?.authorResponseDetails
                              ?.authorResponseAttachments?.length ?? 0}
                            )
                          </ButtonText>
                        </Flex>
                      </Flex>
                    </>
                  )}
                </Flex>
              </Flex>
            </OutlineCard>
          );
        })
      ) : (
        <Flex justifyContent="center" alignItems="center">
          <Text textStyle="textContext">No decision history found</Text>
        </Flex>
      )}
    </>
  );
};
export default DecisionHistoryFullView;
